import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, ChevronRight, ChevronDown, Building2, FolderKanban,
  ScrollText, Package,
} from 'lucide-react';
import clsx from 'clsx';
import { useProjectStore } from '../../store/useProjectStore';
import { useReleaseStore } from '../../store/useReleaseStore';
import { usePIStore } from '../../store/usePIStore';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import type { Project, Release } from '../../types';

const STATUS_COLORS: Record<string, string> = {
  planned: '#3b82f6',
  released: '#22c55e',
  cancelled: '#94a3b8',
};

export default function ReleaseNotesPanel() {
  const { projects } = useProjectStore();
  const { releases, fetchReleases } = useReleaseStore();
  const { sprints, pis, fetchPIs, fetchAllSprints } = usePIStore();
  const { organizations, fetchOrganizations } = useOrgHierarchyStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchOrganizations();
    fetchPIs();
    fetchAllSprints();
    fetchReleases();
  }, [fetchOrganizations, fetchPIs, fetchAllSprints, fetchReleases]);

  const selectedReleaseId = location.pathname.startsWith('/release-notes/')
    ? decodeURIComponent(location.pathname.slice('/release-notes/'.length))
    : null;

  /** Map each project to its releases.
   *
   *  A release carries `projectId` from F3 onward. Until that ships, a release
   *  can only be attributed through its sprint's PI, so it is shown under every
   *  project participating in that PI. Once F3 lands the direct match wins and
   *  the fallback stops firing.
   */
  const releasesByProject = useMemo(() => {
    const sprintToPi = new Map(sprints.map((s) => [s._id, s.piId]));
    const piToProjects = new Map(pis.map((p) => [p._id, p.projectIds || []]));
    const map = new Map<string, Release[]>();

    const add = (projectId: string, release: Release) => {
      const list = map.get(projectId) || [];
      if (!list.some((r) => r._id === release._id)) list.push(release);
      map.set(projectId, list);
    };

    releases.forEach((release) => {
      // Empty releases closed with no content have no notes by construction.
      if (release.closureReason === 'no_content') return;

      if (release.projectId) {
        add(release.projectId, release);
        return;
      }
      const piId = sprintToPi.get(release.sprintId);
      if (!piId) return;
      (piToProjects.get(piId) || []).forEach((pid) => add(pid, release));
    });

    // Newest first
    map.forEach((list) =>
      list.sort((a, b) =>
        (b.plannedDate || b.releaseDate || '').localeCompare(
          a.plannedDate || a.releaseDate || '',
        ),
      ),
    );
    return map;
  }, [releases, sprints, pis]);

  const matchingProjects = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((p) => {
      if (p.name.toLowerCase().includes(term)) return true;
      return (releasesByProject.get(p._id) || []).some(
        (r) =>
          r.version.toLowerCase().includes(term) ||
          (r.name || '').toLowerCase().includes(term),
      );
    });
  }, [projects, search, releasesByProject]);

  const toggle = (set: Set<string>, key: string,
                  apply: (next: Set<string>) => void) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    apply(next);
  };

  const openRelease = (release: Release) => {
    navigate(`/release-notes/${encodeURIComponent(release._id)}`);
  };

  const renderRelease = (release: Release) => {
    const isSelected = release._id === selectedReleaseId;
    const date = release.plannedDate || release.releaseDate;
    return (
      <button
        key={release._id}
        onClick={() => openRelease(release)}
        className={clsx(
          'w-full flex items-center gap-2 pl-9 pr-3 py-2 rounded-lg text-left transition-colors',
          isSelected
            ? 'bg-accent/10 text-accent'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700',
        )}
      >
        <Package size={13} className="shrink-0 opacity-70" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium truncate">
            {release.version}
            {release.releaseType === 'standalone' && (
              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                off-cycle
              </span>
            )}
          </span>
          {date && (
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {date}
            </span>
          )}
        </span>
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: STATUS_COLORS[release.status] || '#94a3b8' }}
          title={release.status}
        />
      </button>
    );
  };

  const renderProject = (project: Project) => {
    const projectReleases = releasesByProject.get(project._id) || [];
    const isExpanded = expandedProjects.has(project._id);

    return (
      <div key={project._id}>
        <button
          onClick={() =>
            toggle(expandedProjects, project._id, setExpandedProjects)
          }
          className="w-full flex items-center gap-2 pl-4 pr-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <FolderKanban size={13} className="text-accent shrink-0" />
          <span className="flex-1 min-w-0 text-sm font-medium truncate">
            {project.displayId && (
              <span className="text-[10px] font-mono text-gray-400 mr-1">
                {project.displayId}
              </span>
            )}
            {project.name}
          </span>
          <span className="text-xs text-gray-400">{projectReleases.length}</span>
        </button>

        {isExpanded && (
          <div className="space-y-0.5">
            {projectReleases.length === 0 ? (
              <p className="text-xs text-gray-400 pl-9 py-1.5">
                No releases yet
              </p>
            ) : (
              projectReleases.map(renderRelease)
            )}
          </div>
        )}
      </div>
    );
  };

  const unassigned = matchingProjects.filter((p) => !p.organizationId);

  return (
    <div className="flex flex-col h-full">
      <div className="p-3">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search projects or versions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
        {organizations.length === 0 && matchingProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <ScrollText size={32} className="mb-2" />
            <p className="text-sm">No releases found</p>
          </div>
        ) : (
          <>
            {organizations.map((org) => {
              const orgProjects = matchingProjects.filter(
                (p) => p.organizationId === org._id,
              );
              if (orgProjects.length === 0 && search) return null;
              const isExpanded = expandedOrgs.has(org._id);

              return (
                <div key={org._id}>
                  <button
                    onClick={() => toggle(expandedOrgs, org._id, setExpandedOrgs)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-750 rounded"
                  >
                    {isExpanded ? (
                      <ChevronDown size={12} />
                    ) : (
                      <ChevronRight size={12} />
                    )}
                    <Building2 size={12} className="text-indigo-400" />
                    <span className="truncate">{org.name}</span>
                    <span className="ml-auto text-gray-400">
                      {orgProjects.length}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="ml-1 space-y-0.5">
                      {orgProjects.length === 0 ? (
                        <p className="text-xs text-gray-400 px-4 py-1">
                          No projects
                        </p>
                      ) : (
                        orgProjects.map(renderProject)
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {unassigned.length > 0 && (
              <div>
                <button
                  onClick={() => toggle(expandedOrgs, '_unassigned', setExpandedOrgs)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-750 rounded"
                >
                  {expandedOrgs.has('_unassigned') ? (
                    <ChevronDown size={12} />
                  ) : (
                    <ChevronRight size={12} />
                  )}
                  <FolderKanban size={12} className="text-gray-400" />
                  <span>Unassigned</span>
                  <span className="ml-auto text-gray-400">
                    {unassigned.length}
                  </span>
                </button>
                {expandedOrgs.has('_unassigned') && (
                  <div className="ml-1 space-y-0.5">
                    {unassigned.map(renderProject)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
